import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  FilePresent as FileIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import apiService from '../services/api';

const ResumeManagementPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const { config } = useConfig();
  
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get('jobId') || '');
  const [selectedJob, setSelectedJob] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (config && getAuthToken) {
      apiService.initialize(config.apiGatewayUrl, getAuthToken);
      loadJobs();
    }
  }, [config, getAuthToken]);

  useEffect(() => {
    if (selectedJobId && jobs.length > 0) {
      const job = jobs.find(j => j.jobId === selectedJobId);
      setSelectedJob(job);
      loadResumesForJob(selectedJobId);
    }
  }, [selectedJobId, jobs]);

  const loadJobs = async () => {
    try {
      setJobsLoading(true);
      const jobsData = await apiService.getJobs();
      setJobs(jobsData);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Jobs loading error:', err);
    } finally {
      setJobsLoading(false);
    }
  };

  const loadResumesForJob = async (jobId) => {
    try {
      setLoading(true);
      const resumesData = await apiService.getResumesForJob(jobId);
      setResumes(resumesData);
    } catch (err) {
      setError('Failed to load resumes');
      console.error('Resumes loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = (event) => {
    const newJobId = event.target.value;
    setSelectedJobId(newJobId);
    setResumes([]);
    setError('');
    setSuccess('');
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !selectedJobId) return;

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const uploadResults = [];
      const totalFiles = files.length;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const result = await apiService.uploadResumeToJob(selectedJobId, file);
          uploadResults.push(result);
          setUploadProgress(((i + 1) / totalFiles) * 100);
        } catch (fileError) {
          console.error(`Failed to upload ${file.name}:`, fileError);
          uploadResults.push({
            success: false,
            fileName: file.name,
            error: fileError.message
          });
        }
      }

      const successCount = uploadResults.filter(r => r.success).length;
      const failCount = uploadResults.filter(r => !r.success).length;

      if (successCount > 0) {
        setSuccess(`Successfully uploaded ${successCount} resume${successCount !== 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`);
        await loadResumesForJob(selectedJobId);
      }

      if (failCount > 0 && successCount === 0) {
        setError(`Failed to upload ${failCount} resume${failCount !== 1 ? 's' : ''}`);
      }

    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear file input
      event.target.value = '';
    }
  };

  const handleFindCandidates = () => {
    if (resumes.length === 0) {
      setError('No resumes available. Please upload resumes first.');
      return;
    }
    navigate(`/candidate-matching?jobId=${selectedJobId}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={2}>
          <PersonIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Resume Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Upload and manage candidate resumes for your job postings. Organize resumes by job for efficient candidate matching.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Job Selection and Upload Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Job
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Choose Job</InputLabel>
              <Select
                value={selectedJobId}
                onChange={handleJobChange}
                label="Choose Job"
                disabled={jobsLoading}
              >
                {jobs.map((job) => (
                  <MenuItem key={job.jobId} value={job.jobId}>
                    {job.title} - {job.department}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedJob && (
              <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    <WorkIcon sx={{ mr: 1, fontSize: 16 }} />
                    Job Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{selectedJob.title}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedJob.department} • {selectedJob.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status: <Chip label={selectedJob.status || 'active'} size="small" color="success" />
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Upload Resumes
            </Typography>
            
            <input
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              id="resume-upload"
              multiple
              type="file"
              onChange={handleFileUpload}
              disabled={!selectedJobId || uploading}
            />
            <label htmlFor="resume-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={!selectedJobId || uploading}
                fullWidth
                sx={{ mb: 2 }}
              >
                {uploading ? 'Uploading...' : 'Upload Resumes'}
              </Button>
            </label>

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  {Math.round(uploadProgress)}% Complete
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Supported formats: PDF, DOC, DOCX, TXT
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Multiple files can be selected at once
            </Typography>

            {selectedJobId && resumes.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="contained"
                  startIcon={<AssessmentIcon />}
                  onClick={handleFindCandidates}
                  fullWidth
                  color="success"
                >
                  Find Candidates ({resumes.length} resumes)
                </Button>
              </>
            )}
          </Paper>
        </Grid>

        {/* Resume List Panel */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Uploaded Resumes
              {selectedJobId && (
                <Chip 
                  label={`${resumes.length} files`} 
                  size="small" 
                  sx={{ ml: 2 }} 
                  color="primary"
                />
              )}
            </Typography>

            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}

            {!loading && !selectedJobId && (
              <Box textAlign="center" py={4}>
                <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a Job
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a job from the dropdown to view and manage its resumes.
                </Typography>
              </Box>
            )}

            {!loading && selectedJobId && resumes.length === 0 && (
              <Box textAlign="center" py={4}>
                <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Resumes Uploaded
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload candidate resumes to start building your candidate pool for this job.
                </Typography>
              </Box>
            )}

            {!loading && resumes.length > 0 && (
              <List>
                {resumes.map((resume, index) => (
                  <ListItem
                    key={resume.id || index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" component="span">
                            {resume.candidateName}
                          </Typography>
                          <Chip 
                            label={resume.fileName} 
                            size="small" 
                            variant="outlined" 
                            sx={{ ml: 1 }} 
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Uploaded: {formatDate(resume.uploadedAt)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Size: {formatFileSize(resume.size || 0)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="view"
                        onClick={() => {/* Handle view resume */}}
                      >
                        <ViewIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ResumeManagementPage;
